import { format, isDate, Locale } from 'date-fns';
import { DEFAULT_DATE_FORMATS } from '../types/language';
import {
  LocalizationSetting,
  LocalizedDateTimeFormatKey,
} from '../types/localization-setting';

const DATABASE_DATE_FORMAT = 'yyyy-MM-dd';

/**
 * Do not use this helper directly in code.
 * Prefer the localization pipes so formatting stays reactive to localization state changes.
 *
 * See:
 * - `libs/localization/src/pipes/localized-date-range-format.pipe.ts`
 * - `libs/localization/src/pipes/localized-time-range-format.pipe.ts`
 */
export function toLocalizedDateTimeRangeFormat(
  {
    start,
    end,
  }: {
    start: string | Date | number;
    end: string | Date | number;
  },
  localizationSetting: LocalizationSetting | null,
  formatType: LocalizedDateTimeFormatKey,
  locale: Locale,
): string {
  return `${toLocalizedDateTimeFormat(start, localizationSetting, formatType, locale)} - ${toLocalizedDateTimeFormat(end, localizationSetting, formatType, locale)}`;
}

/**
 * Do not use this helper directly in code.
 * Prefer the localization pipes so formatting stays reactive to localization state changes.
 *
 * See:
 * - `libs/localization/src/pipes/localized-date-time-format.pipe.ts`
 * - `libs/localization/src/pipes/localized-date-format.pipe.ts`
 * - `libs/localization/src/pipes/localized-time-format.pipe.ts`
 */
export function toLocalizedDateTimeFormat(
  value: string | Date | number,
  localizationSetting: LocalizationSetting | null,
  formatType: LocalizedDateTimeFormatKey,
  locale: Locale,
): string {
  if (localizationSetting) {
    return formatDateTime(value, localizationSetting[formatType], locale);
  } else {
    return formatDateTime(value, DEFAULT_DATE_FORMATS[formatType], locale);
  }
}

/**
 * Do not use this helper directly in code.
 * Prefer the localization pipes so formatting stays reactive to localization state changes.
 *
 * See:
 * - `libs/localization/src/pipes/localized-time-date-format.pipe.ts`
 */
export function toLocalizedTimeDateFormat(
  value: string | number | Date,
  localizationSetting: LocalizationSetting | null,
  locale: Locale,
): string {
  return `${toLocalizedDateTimeFormat(value, localizationSetting, 'timeFormat', locale)} - ${toLocalizedDateTimeFormat(value, localizationSetting, 'dateFormat', locale)}`;
}

export function toDatabaseDateFormat(value: string | Date): string {
  return format(new Date(value), DATABASE_DATE_FORMAT);
}

function formatDateTime(
  value: string | Date | number,
  formatType: string,
  locale?: Locale,
): string {
  try {
    const options = locale ? { locale } : undefined;
    return isDate(value)
      ? format(value as Date, formatType, options)
      : format(new Date(value), formatType, options);
  } catch (error) {
    console.trace(error);
    return '';
  }
}
